import { AsyncResult, Action, ApiAction } from '../../index';
import api from '../../redux-api';

// Utils
import { uniqueArray } from '../../../utils/objects';

// Types
import { BenchmarkStoreState } from '../types/benchmark-store';
import { getMonthEndDataOption } from '../../../utils/comparison';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import Nullable from '@/types/nullable';
import Benchmark from '@/types/benchmark';
import Fund from '@/types/fund';
import { batch } from 'react-redux';

// TODO -- Merge with fund-store (CON-289)

// Initial State
const INITIAL_STATE = {
  benchmarks: {},
  benchmarksPMED: {},
  benchmarksRMED: {},
  benchmarksPQED: {},
  benchmarksRQED: {},
};

// Actions
const FETCH_BENCHMARK_BY_ID: ApiAction = {
  type: 'connect/benchmarks/store/fetch',
  endpoint: 'benchmarks/:id/',
};

const FETCH_MANY_BENCHMARKS: ApiAction = {
  type: 'connect/benchmarks/store/fetch-many',
  endpoint: 'benchmarks/',
};

const BENCHMARK_STORE_GET = 'connect/benchmarks/store/get';
const BENCHMARK_STORE_GET_MANY = 'connect/benchmarks/store/get-many';
const BENCHMARK_STORE_ADD = 'connect/benchmarks/store/add';

// Reducer
export default function reducer(state: BenchmarkStoreState = INITIAL_STATE, action: Action): BenchmarkStoreState {
  switch (action.type) {
    case BENCHMARK_STORE_ADD:
      switch (action.monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          return {
            ...state,
            benchmarksRMED: { ...state.benchmarksRMED, [action.benchmark.lipperId]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          return {
            ...state,
            benchmarksPMED: { ...state.benchmarksPMED, [action.benchmark.lipperId]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          return {
            ...state,
            benchmarksRQED: { ...state.benchmarksRQED, [action.benchmark.lipperId]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          return {
            ...state,
            benchmarksPQED: { ...state.benchmarksPQED, [action.benchmark.lipperId]: action.benchmark },
          };
        default:
          return {
            ...state,
            benchmarks: { ...state.benchmarks, [action.benchmark.lipperId]: action.benchmark },
          };
      }
    default:
      return state;
  }
}

// Action Creators

/**
 * Retrieve Benchmark data either from memory, local cache or directly from the API
 * @param id Benchmark's unique identifier
 */
export const getBenchmarkData =
  (id: string | null): AsyncResult<Nullable<Benchmark>> =>
  (dispatch, getState) => {
    dispatch({ type: BENCHMARK_STORE_GET, id });
    if (id == null) {
      return Promise.resolve(null);
    }

    const state = getState();

    const monthEndDataOption = getMonthEndDataOption(state.comparison);

    // See if we got it in State
    let stateBenchmark;
    switch (monthEndDataOption) {
      case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
        stateBenchmark = getState().benchmarks.store.benchmarksRMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
        stateBenchmark = getState().benchmarks.store.benchmarksPMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
        stateBenchmark = getState().benchmarks.store.benchmarksRQED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
        stateBenchmark = getState().benchmarks.store.benchmarksPQED[id];
        break;
      default:
        stateBenchmark = getState().benchmarks.store.benchmarks[id];
        break;
    }
    if (stateBenchmark) {
      return Promise.resolve(stateBenchmark);
    }

    // Important to return null in case of an API error, not to break the standard flow
    // Null benchmarks only prevent proper risk metrics calcs
    return api
      .get(FETCH_BENCHMARK_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) })
      .then(({ benchmark }: { benchmark: Benchmark }) => {
        dispatch({ type: BENCHMARK_STORE_ADD, benchmark, monthEndDataOption });
        return benchmark;
      })
      .catch(() => null);
  };

/**
 * Retrieve all benchmarks data associated with the requested fund,
 * either from local cache, memory or directly from the API.
 *
 * @param fund Fund for which the benchmark data should be retrieved.
 * @returns Array of benchmarks.
 */
export const getFundBenchmarks =
  (fund: Fund): AsyncResult<Benchmark[]> =>
  dispatch => {
    const ids = [fund.benchmarkLipperId, fund.riskFreeLipperId];
    const uniqueBenchmarkIds = uniqueArray((ids.filter(id => id != null) as number[]).map(id => id.toString()));

    if (uniqueBenchmarkIds.length === 0) {
      return Promise.resolve([]);
    }
    return dispatch(getManyBenchmarksData(uniqueBenchmarkIds));
  };

/**
 * Retrieve multiple Benchmarks data either from memory, local cache or directly from the API
 * @param ids Benchmarks' unique identifiers
 */
export const getManyBenchmarksData =
  (rawIds: string[]): AsyncResult<Benchmark[]> =>
  (dispatch, getState) => {
    const ids: string[] = rawIds.filter(id => id != null && id !== '');
    dispatch({ type: BENCHMARK_STORE_GET_MANY, ids });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const remaining = new Set(ids);
    const benchmarksData = [] as Benchmark[];

    for (const id of ids) {
      // See if we got it in State
      let stateBenchmark;
      switch (monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          stateBenchmark = getState().benchmarks.store.benchmarksRMED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          stateBenchmark = getState().benchmarks.store.benchmarksPMED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          stateBenchmark = getState().benchmarks.store.benchmarksRQED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          stateBenchmark = getState().benchmarks.store.benchmarksPQED[id];
          break;
        default:
          stateBenchmark = getState().benchmarks.store.benchmarks[id];
          break;
      }
      if (stateBenchmark) {
        benchmarksData.push(stateBenchmark);
        remaining.delete(id);
        continue;
      }
    }

    // Fetch the rest from the API if we're missing any
    // Important to return null in case of an API error, not to break the standard flow
    // Null benchmarks only prevent proper risk metrics calcs
    if (remaining.size) {
      return api
        .get(FETCH_MANY_BENCHMARKS, { ids: Array.from(remaining.values()).join('-'), ...(monthEndDataOption && { monthEndDataOption }) })
        .then(({ benchmarks }: { benchmarks: Benchmark[] }) => {
          batch(() => {
            for (const benchmark of benchmarks) {
              dispatch({ type: BENCHMARK_STORE_ADD, benchmark, monthEndDataOption });
            }
          });

          return benchmarksData.concat(benchmarks);
        })
        .catch(() => benchmarksData);
    }

    // Return the cached benchmarks if no API call is required
    return Promise.resolve(benchmarksData);
  };

/**
 * Retrieve all benchmarks data associated with the requested funds,
 * either from local cache, memory or directly from the API.
 *
 * @param funds Funds for which the benchmark data should be retrieved.
 * @returns Array of benchmarks.
 */
export const getFundsBenchmarks =
  (funds: Fund[]): AsyncResult<Benchmark[]> =>
  dispatch => {
    const benchmarkIds = funds.reduce((ids: (number | undefined)[], fund) => [...ids, fund.benchmarkLipperId, fund.riskFreeLipperId], []);

    const uniqueBenchmarkIds = uniqueArray((benchmarkIds.filter(id => id != null) as number[]).map(id => id.toString()));

    if (uniqueBenchmarkIds.length === 0) {
      return Promise.resolve([]);
    }
    return dispatch(getManyBenchmarksData(uniqueBenchmarkIds));
  };
